import React, { useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { StatusCodes } from 'http-status-codes';
import {
  BankOutlined,
  ClockCircleOutlined,
  LockOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import ActionBar from '../../../components/common/ActionBar';
import BrandHeader from '../../../components/common/BrandHeader';
import DetailRow from '../../../components/common/DetailRow';
import KeyTerm from '../../../components/common/KeyTerm';
import Note from '../../../components/common/Note';
import StepBar from '../../../components/common/StepBar';
import ContactHelpText from '../../../components/common/ContactHelpText';
import TermsAccordion, {
  areAllSectionsAgreed,
  TermsSection,
} from '../../../components/common/TermsAccordion';
import {
  Card,
  CardBody,
  Divider,
  PageHead,
  PageShell,
  PageSubtitle,
  PageTitle,
  SectionTitle,
} from '../../../components/common/Card';
import {
  DocumentType,
  IssuerMeta,
  IssuerName,
  LineItem,
  LineItemAmount,
  LineItemDescription,
  LineItemDetail,
  MetaGrid,
  MetaItem,
  MetaLabel,
  MetaValue,
} from '../../../components/common/DocumentCard';
import {
  BUSINESS_ABN,
  BUSINESS_DESCRIPTOR,
  BUSINESS_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  PAYMENT_GATEWAY_NAME,
  SERVICE_DESCRIPTION,
} from '../../../constants/business';
import {
  PRE_APPT_COUNTER_PREFIX,
  PRE_APPT_STEPS,
  PRE_APPT_STEP_NUMBER,
} from '../../../constants/preApptConstants';
import { ROUTE_PREFIX_PRE_APPT } from '../../../constants/routes';
import {
  PAYMENT_BANK_TRANSFER,
  PAYMENT_TERMS_SECTIONS,
  TERMS_NOT_AGREED_MESSAGE,
  TERMS_VERSION,
} from '../../../constants/terms';
import momentTimezone from 'moment-timezone';
import InlineAlert from '../../../components/common/InlineAlert';
import { TENANT_TIMEZONE } from '../../../constants/business';
import { recordPaymentConsent } from '../../../API/appointment';
import { fetchVerifiedWorkflowById } from '../../../slices/workflow';
import { clearPatientAccessToken } from '../../../utils/patientAccessStorage';
import { scrollToElement } from '../../../utils/scrollUtils';
import {
  formatAmountFromCents,
  formatAppointmentDateTime,
} from '../../../utils/appointmentDisplayUtils';

// The bank transfer note names the invoices email; make it a tappable link.
const renderWithEmailLink = (text) => {
  const [before, after] = text.split(CONTACT_EMAIL);
  if (after === undefined) return text;

  return (
    <>
      {before}
      <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      {after}
    </>
  );
};

const PAYMENT_DESCRIPTION = 'Telecare Appointment Payment';
const ISSUED_DATE_FORMAT = 'DD/MM/YYYY';
const CONSENT_FAILURE_MESSAGE = `We could not save your agreement to the terms, so we have not taken you to the payment page. Please try again, or call Telecare on (${CONTACT_PHONE}) if this keeps happening.`;

const buildPaymentLink = ({
  baseLink,
  uiBaseUrl,
  workflowId,
  amount,
  clientName,
}) => {
  const successUrl = `${uiBaseUrl}/self-service/pre-appt/${workflowId}/finish-payment`;

  return (
    `${baseLink}?amount=${amount}` +
    `&description=${encodeURIComponent(PAYMENT_DESCRIPTION)}` +
    '&amount_editable=false' +
    `&success_url=${successUrl}` +
    `&field1label=transaction_id&field1value=${workflowId}` +
    `&field2label=client_name&field2value=${encodeURIComponent(clientName)}`
  );
};

const PaymentConfirmation = () => {
  const dispatch = useDispatch();
  const { workflowId } = useParams();
  const history = useHistory();
  const { payment, client, specialist, appointment } = useSelector(
    ({ workflow }) => workflow.data
  );
  const [agreements, setAgreements] = useState({});
  const [showTermsError, setShowTermsError] = useState(false);
  const [isRecordingConsent, setIsRecordingConsent] = useState(false);
  const [consentFailureMessage, setConsentFailureMessage] = useState('');
  const termsRef = useRef(null);

  const feeInCents = payment?.paymentInfo?.fee;
  const totalPayable = formatAmountFromCents(feeInCents);
  const clientName = `${client?.firstName || ''} ${
    client?.lastName || ''
  }`.trim();
  const appointmentDateTime = formatAppointmentDateTime(
    appointment?.startDateTime
  );
  const clinicianName = specialist
    ? `Dr. ${specialist.firstName} ${specialist.lastName}`
    : '';
  const hasAgreedToAllTerms = areAllSectionsAgreed(
    PAYMENT_TERMS_SECTIONS,
    agreements
  );

  const handleAgreementChange = (sectionKey, isAgreed) => {
    setAgreements((current) => ({ ...current, [sectionKey]: isAgreed }));
    setShowTermsError(false);
  };

  const handlePay = async () => {
    if (!hasAgreedToAllTerms) {
      setShowTermsError(true);
      scrollToElement(termsRef.current);
      return;
    }

    setIsRecordingConsent(true);
    setConsentFailureMessage('');

    const { error: recordConsentError, httpStatus: recordConsentHttpStatus } =
      await recordPaymentConsent({
        workflowId,
        termsVersion: TERMS_VERSION,
        agreedSectionKeys: PAYMENT_TERMS_SECTIONS.filter(({ key }) =>
          Boolean(agreements[key])
        ).map(({ key }) => key),
      });

    if (recordConsentHttpStatus === StatusCodes.UNAUTHORIZED) {
      clearPatientAccessToken(workflowId);
      dispatch(fetchVerifiedWorkflowById(workflowId));
      return;
    }

    if (recordConsentError) {
      setIsRecordingConsent(false);
      setConsentFailureMessage(CONSENT_FAILURE_MESSAGE);
      scrollToElement(termsRef.current);
      return;
    }

    window.location.href = buildPaymentLink({
      baseLink: process.env.REACT_APP_PIN_PAYMENT_BASE_LINK,
      uiBaseUrl: process.env.REACT_APP_UI_BASE_URL,
      workflowId,
      amount: Number(feeInCents) / 100,
      clientName,
    });
  };

  return (
    <>
      <BrandHeader />
      <PageShell>
        <StepBar
          steps={PRE_APPT_STEPS}
          currentStepNumber={PRE_APPT_STEP_NUMBER.PAYMENT}
          counterPrefix={PRE_APPT_COUNTER_PREFIX}
        />

        <PageHead>
          <PageTitle>Payment</PageTitle>
          <PageSubtitle>
            Review what you&apos;re paying for, then continue to our secure
            payment page.
          </PageSubtitle>
        </PageHead>

        <Card>
          <CardBody>
            <DocumentType>Payment request</DocumentType>
            <IssuerName>{BUSINESS_NAME}</IssuerName>
            <IssuerMeta>{`${BUSINESS_ABN} · ${BUSINESS_DESCRIPTOR}`}</IssuerMeta>
            <MetaGrid>
              <MetaItem>
                <MetaLabel>Billed to</MetaLabel>
                <MetaValue>{clientName}</MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Issued</MetaLabel>
                <MetaValue>
                  {momentTimezone()
                    .tz(TENANT_TIMEZONE)
                    .format(ISSUED_DATE_FORMAT)}
                </MetaValue>
              </MetaItem>
              <MetaItem>
                <MetaLabel>Reference</MetaLabel>
                <MetaValue>{workflowId}</MetaValue>
              </MetaItem>
            </MetaGrid>
          </CardBody>

          <Divider />

          <CardBody>
            <SectionTitle>Service details</SectionTitle>
            <LineItem>
              <div>
                <LineItemDescription>{SERVICE_DESCRIPTION}</LineItemDescription>
                {clinicianName && (
                  <LineItemDetail>
                    {specialist?.speciality
                      ? `${clinicianName} · ${specialist.speciality}`
                      : clinicianName}
                  </LineItemDetail>
                )}
                {appointmentDateTime && (
                  <LineItemDetail>{appointmentDateTime}</LineItemDetail>
                )}
              </div>
              <LineItemAmount>{totalPayable}</LineItemAmount>
            </LineItem>
            <DetailRow
              label="Total payable"
              value={totalPayable}
              currency="AUD"
              emphasised
            />
          </CardBody>
        </Card>

        <Note>
          <b>Medicare rebate:</b> the rebate may vary based on your eligibility.
          If you&apos;re eligible we lodge your claim within 3 business days of
          you attending your appointment, and Medicare pays the rebate straight
          into your nominated bank account. The final item number is decided by
          your clinician, so this amount may be adjusted afterwards — we&apos;ll
          contact you if it changes.
        </Note>

        <Card>
          <CardBody>
            <SectionTitle>Before you pay</SectionTitle>
            <KeyTerm icon={<ClockCircleOutlined />}>
              <b>Paying now secures your appointment.</b> Your place is held
              once we receive your payment.
            </KeyTerm>
            <KeyTerm icon={<UnorderedListOutlined />}>
              Cancelling within <b>2 business days</b> without a valid medical
              reason will incur a <b>fee ($150)</b>.
            </KeyTerm>
            <KeyTerm icon={<LockOutlined />}>
              Card details are handled by <b>{PAYMENT_GATEWAY_NAME}</b>, an
              Australian payment gateway. Telecare never stores your card
              number.
            </KeyTerm>
          </CardBody>
        </Card>

        <TermsSection ref={termsRef}>
          <TermsAccordion
            groupTitle="Full terms"
            groupHint="Please open each section, read it, and tick to confirm."
            sections={PAYMENT_TERMS_SECTIONS}
            agreements={agreements}
            onAgreementChange={handleAgreementChange}
            errorMessage={TERMS_NOT_AGREED_MESSAGE}
            showError={showTermsError}
          />
          {consentFailureMessage && (
            <InlineAlert>{consentFailureMessage}</InlineAlert>
          )}
        </TermsSection>

        <Card>
          <CardBody>
            <SectionTitle>{PAYMENT_BANK_TRANSFER.title}</SectionTitle>
            <KeyTerm icon={<BankOutlined />}>
              {renderWithEmailLink(PAYMENT_BANK_TRANSFER.text)}
            </KeyTerm>
          </CardBody>
        </Card>

        <ContactHelpText />
      </PageShell>

      <ActionBar
        amountLabel="Total payable"
        amount={totalPayable}
        currency="AUD"
        onBack={() => history.push(`${ROUTE_PREFIX_PRE_APPT}/${workflowId}`)}
        nextLabel="Pay now"
        onNext={handlePay}
        isLoading={isRecordingConsent}
        consentLine="By continuing you agree to the payment terms above."
        secureLine={`Secured by ${PAYMENT_GATEWAY_NAME}`}
      />
    </>
  );
};

export default PaymentConfirmation;
